import { useTranslate } from '@refinedev/core';
import { useTable } from '@refinedev/antd';
import { Card, Table, Tooltip, Typography } from 'antd';
import type { FCMTokenOut } from '@/api/generated/schemas';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

const { Text } = Typography;

export const UserFcmTokensTab = ({ userId }: { userId: number }) => {
  const t = useTranslate();
  const { tableProps, sorters, result } = useTable<FCMTokenOut>({
    resource: 'account.fcm_tokens',
    filters: {
      permanent: [{ field: 'user_id', operator: 'eq', value: userId }],
    },
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 10 },
    syncWithLocation: false,
  });

  return (
    <Card title={`${t('iam.users.tabs.fcm_tokens')} (${result?.total ?? 0})`}>
      <Table<FCMTokenOut>
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
        size="small"
      >
        <Table.Column<FCMTokenOut>
          dataIndex="id"
          title={t('base.fields.id')}
          width={90}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowIconLink resource="account.fcm_tokens" id={value}>
              {value}
            </ShowIconLink>
          )}
        />
        <Table.Column<FCMTokenOut>
          dataIndex="platform"
          title={t('account.fcm_tokens.fields.platform')}
          render={(value: string | null) => orDash(value)}
        />
        <Table.Column<FCMTokenOut>
          dataIndex="app_version"
          title={t('account.fcm_tokens.fields.app_version')}
          render={(value: string | null) => orDash(value)}
        />
        <Table.Column<FCMTokenOut>
          dataIndex="ip_address"
          title={t('account.fcm_tokens.fields.ip_address')}
          render={(value: string | null) => orDash(value)}
        />
        <Table.Column<FCMTokenOut>
          dataIndex="last_used_at"
          title={t('account.fcm_tokens.fields.last_used_at')}
          render={(value: string | null) => orDash(formatDateTime(value))}
        />
        <Table.Column<FCMTokenOut>
          dataIndex="token"
          title={t('account.fcm_tokens.fields.token')}
          render={(value: string) => (
            <Tooltip title={value}>
              <Text
                code
                copyable={{ text: value }}
                ellipsis
                style={{ maxWidth: 160, display: 'inline-block' }}
              >
                {value}
              </Text>
            </Tooltip>
          )}
        />
        <Table.Column<FCMTokenOut>
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          render={(value: string) => orDash(formatDateTime(value))}
        />
      </Table>
    </Card>
  );
};
